/**
 * Component Reorder Modal
 * Modal dialog for reordering polls and text fields within a channel
 */

import React, {
    useCallback,
    useState,
    useMemo,
    useRef,
    useEffect,
} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import { IPoll, ITextFieldCollection } from "@Interfaces";
import { useDispatch, useSelector } from "react-redux";
import { RootStateType } from "@Store";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { ChannelActions } from "@Store";
import DraggableComponentList, {
    ComponentItem,
} from "../DraggableComponentList";
import { actions as channelReducerActions } from "@Store/Reducers/channels";
import { reorderChannelComponents } from "@Services/API/Reordering";
import { buildComponentItems } from "../../Utils/componentOrderUtils";

export interface ComponentReorderModalProps {
    channelId: string;
}

const ComponentReorderModal: React.FC<ComponentReorderModalProps> = ({
    channelId,
}) => {
    const dispatch = useDispatch<any>();
    const open = useSelector((state: RootStateType) => state.dialog.open);

    // Get the selected channel from Redux
    const selectedChannel = useSelector((state: RootStateType) => {
        const channels = state.channels.channels || [];
        return channels.find((ch) => ch._id === channelId);
    });

    const [temporaryItems, setTemporaryItems] = useState<ComponentItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const polls = selectedChannel?.polls || [];
    const textFields = selectedChannel?.text_fields || [];
    const componentOrder = selectedChannel?.componentOrder;

    // Create initial items using the saved component order
    // This respects the interleaved order stored in the channel
    const initialItems = useMemo(
        () => buildComponentItems(polls, textFields, componentOrder),
        [polls, componentOrder, textFields]
    );

    const initialItemsRef = useRef<ComponentItem[]>(initialItems);
    const prevOpenRef = useRef<boolean>(false);
    const isMountedRef = useRef<boolean>(true);
    const runIfMounted = useCallback((action: () => void) => {
        if (isMountedRef.current) {
            action();
        }
    }, []);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    React.useEffect(() => {
        const wasOpen = prevOpenRef.current;

        if (!wasOpen && open) {
            initialItemsRef.current = initialItems;
            setTemporaryItems(initialItems);
            setError(null);
        }

        prevOpenRef.current = open;
    }, [open, initialItems]);

    const handleReorder = useCallback((newItems: ComponentItem[]) => {
        setTemporaryItems(newItems);
        setError(null);
    }, []);

    const handleSave = async () => {
        try {
            runIfMounted(() => setIsLoading(true));
            runIfMounted(() => setError(null));

            // Convert temporaryItems to the unified format with type information
            const orderedItems = temporaryItems.map((item) => ({
                type: item.type as "poll" | "textfield",
                id: item.id,
            }));

            // Send reorder request to backend with the unified order
            await reorderChannelComponents(channelId, orderedItems);

            const orderedPolls = temporaryItems
                .filter((item) => item.type === "poll")
                .map((item) => polls.find((poll) => poll._id === item.id))
                .filter((poll): poll is IPoll => Boolean(poll));

            const orderedTextFields = temporaryItems
                .filter((item) => item.type === "textfield")
                .map((item) =>
                    textFields.find((field) => field._id === item.id)
                )
                .filter((field): field is ITextFieldCollection =>
                    Boolean(field)
                );

            dispatch(
                channelReducerActions.storePolls({
                    channel_id: channelId,
                    polls: orderedPolls,
                    text_fields: orderedTextFields,
                })
            );

            dispatch(
                channelReducerActions.setChannelComponentOrder({
                    channelId,
                    order: temporaryItems.map((item) => ({
                        id: item.id,
                        type: item.type,
                    })),
                })
            );

            initialItemsRef.current = temporaryItems;

            // Close modal and refresh data in background
            dispatch(dialogActions.hideDialog());

            // Refresh channel data in the background (don't wait for it)
            dispatch(ChannelActions.ensureChannelData(channelId) as any);
        } catch (err) {
            const errorMessage =
                err instanceof Error
                    ? err.message
                    : "Failed to reorder components";
            runIfMounted(() => setError(errorMessage));
        } finally {
            runIfMounted(() => setIsLoading(false));
        }
    };

    const handleCancel = () => {
        setTemporaryItems(initialItemsRef.current);
        setError(null);
        dispatch(dialogActions.hideDialog());
    };

    // Check if order has changed
    const hasChanges = useMemo(() => {
        return (
            JSON.stringify(temporaryItems) !==
            JSON.stringify(initialItemsRef.current)
        );
    }, [temporaryItems]);

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle
                style={{
                    paddingRight: "40px",
                    background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                }}
            >
                <div style={{ fontWeight: 600, fontSize: "20px" }}>
                    Reorder Components
                </div>
                <div
                    style={{ fontSize: "13px", opacity: 0.9, marginTop: "4px" }}
                >
                    Drag and drop to change the order of polls and text fields
                </div>
                <IconButton
                    onClick={handleCancel}
                    style={{
                        position: "absolute",
                        right: "8px",
                        top: "8px",
                        color: "rgba(255, 255, 255, 0.7)",
                    }}
                    size="large"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent style={{ maxHeight: "600px", overflowY: "auto" }}>
                {error && (
                    <Alert severity="error" style={{ marginBottom: "16px" }}>
                        {error}
                    </Alert>
                )}

                <div style={{ marginTop: "16px" }}>
                    <DraggableComponentList
                        items={temporaryItems}
                        onReorder={handleReorder}
                    />
                </div>
            </DialogContent>
            <DialogActions style={{ padding: "16px 24px", gap: "12px" }}>
                <Button
                    onClick={handleCancel}
                    disabled={isLoading}
                    variant="outlined"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={isLoading || !hasChanges}
                    variant="contained"
                    color="primary"
                >
                    {isLoading ? (
                        <>
                            <CircularProgress
                                size={20}
                                style={{ marginRight: "8px" }}
                            />
                            Saving...
                        </>
                    ) : (
                        "Save"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ComponentReorderModal;
